// Sidebar.jsx - Professional Modern Sidebar
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import SimpleBar from "simplebar-react";
import logo from "../../assets/images/favicon copy.ico";
import brandName from "../../assets/images/logo.png";
import SupportDropdown from "../CommonForBoth/TopbarDropdown/SupportDropdown";
import defaultProfile from "../../assets/images/users/default-avatar.jpg";
import { verifyData } from "../../pages/Common/CommonFunctions/commonFunctions";
import { FiChevronDown, FiMessageSquare, FiMenu } from "react-icons/fi";

// Default icon mapping based on menu names
const defaultIcons = {
  dashboard: "mdi-view-dashboard",
  candidates: "mdi-account-group",
  candidate: "mdi-account-search",
  jobs: "mdi-briefcase",
  job: "mdi-briefcase-outline",
  team: "mdi-account-multiple",
  "my team": "mdi-account-group",
  myteam: "mdi-account-group",
  recruiter: "mdi-handshake",
  recruiters: "mdi-account-tie",
  settings: "mdi-cog",
  setting: "mdi-cog-outline",
  reports: "mdi-chart-bar",
  analytics: "mdi-chart-line",
  email: "mdi-email",
  mail: "mdi-email-outline",
  import: "mdi-file-import",
  export: "mdi-file-export",
  match: "mdi-account-switch",
  add: "mdi-plus-circle",
  "add new": "mdi-plus-circle-outline",
  view: "mdi-eye",
  "view all": "mdi-eye-outline",
  edit: "mdi-pencil",
  delete: "mdi-delete",
  logout: "mdi-logout",
  profile: "mdi-account-circle",
  help: "mdi-help-circle",
  support: "mdi-lifebuoy",
  notification: "mdi-bell",
  notifications: "mdi-bell-outline",
  message: "mdi-message-text",
  messages: "mdi-message-outline",
  calendar: "mdi-calendar",
  search: "mdi-magnify",
  filter: "mdi-filter",
  home: "mdi-home",
  organization: "mdi-domain",
  organizations: "mdi-domain",
  user: "mdi-account",
  users: "mdi-account-multiple",
  role: "mdi-shield-account",
  roles: "mdi-shield-account-outline",
  permission: "mdi-shield-key",
  permissions: "mdi-shield-key-outline",
  template: "mdi-file-document",
  templates: "mdi-file-document-multiple",
  workflow: "mdi-sitemap",
  automation: "mdi-robot",
  integration: "mdi-puzzle",
  integrations: "mdi-puzzle-outline",
  billing: "mdi-credit-card",
  subscription: "mdi-card-account-details",
  api: "mdi-api",
  webhook: "mdi-webhook",
  audit: "mdi-clipboard-text",
  log: "mdi-text-box",
  logs: "mdi-text-box-multiple",
};

// Helper function to get icon with fallback
const getMenuIcon = (menuName, existingIcon) => {
  if (existingIcon) return existingIcon;
  const lowerName = menuName?.toLowerCase()?.trim() || "";
  return defaultIcons[lowerName] || "mdi-circle-medium";
};

const normalizePath = (path) => (path || "").split("?")[0].replace(/\/$/, "");

const isMatchingPath = (currentPath, targetPath) => {
  if (!targetPath || targetPath === "#") {
    return false;
  }

  const normalizedCurrentPath = normalizePath(currentPath);
  const normalizedTargetPath = normalizePath(targetPath);

  return (
    normalizedCurrentPath === normalizedTargetPath ||
    normalizedCurrentPath.startsWith(`${normalizedTargetPath}/`)
  );
};

const Sidebar = (props) => {
  // Refs
  const scrollRef = useRef();
  const sidebarRef = useRef();
  const profileRef = useRef();
  const settingsRef = useRef();
  const submenuRef = useRef();
  const footerRef = useRef();

  // State
  const [isCollapsed, setIsCollapsed] = useState(props.collapsed ?? false);
  const [isHovered, setIsHovered] = useState(false);
  const [activeMenuIndex, setActiveMenuIndex] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Get user data
  const user = JSON.parse(localStorage.getItem("authUser"));

  // Extract and process menus
  const extractedLeftMenus = [...(user?.headerMenu?.leftMenus || [])]
    .filter((m) => !m?.userName && m?.name !== "[UserName]")
    .sort((a, b) => (a?.priority || 0) - (b?.priority || 0));

  const reportsIndex = extractedLeftMenus.findIndex(m => m.name && m.name.toLowerCase() === "reports");
  const plansMenu = {
    id: "plans",
    name: "Plans",
    url: "/plans",
    icon: "mdi-currency-usd",
    subMenus: []
  };

  const leftMenus = [...extractedLeftMenus];
  if (reportsIndex !== -1 && !leftMenus.some(m => m.id === "plans")) {
    leftMenus.splice(reportsIndex, 0, plansMenu);
  } else if (!leftMenus.some(m => m.id === "plans")) {
    leftMenus.push(plansMenu);
  }

  // Inject "Agent Management" link
  if (!leftMenus.some(m => m.id === "agent-management")) {
    const agentMgmtMenu = {
      id: "agent-management",
      name: "Agent Management",
      url: "/agent-management",
      icon: "mdi-robot",
      subMenus: [],
    };
    leftMenus.push(agentMgmtMenu);
  }

  const settingsMenu = (user?.headerMenu?.rightMenus || []).find(
    (m) =>
      m?.icon === "mdi-settings-outline" ||
      m?.name?.toLowerCase() === "settings",
  );

  // Close all submenus when sidebar collapses
  useEffect(() => {
    if (isCollapsed && !isHovered) {
      setActiveMenuIndex(null);
      setSettingsOpen(false);
      setProfileOpen(false);
    }
  }, [isCollapsed, isHovered]);

  // Sync with parent component props
  useEffect(() => {
    if (typeof props.collapsed !== "undefined") {
      setIsCollapsed(props.collapsed);
    }
  }, [props.collapsed]);

  // Handle hover for collapsed state
  const handleMouseEnter = () => {
    if (isCollapsed && window.innerWidth > 768) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = (e) => {
    // Don't collapse if hovering over footer submenu
    if (submenuRef.current?.contains(e.relatedTarget)) {
      return;
    }
    if (isCollapsed && window.innerWidth > 768) {
      setIsHovered(false);
      setActiveMenuIndex(null);
      setSettingsOpen(false);
      setProfileOpen(false);
    }
  };

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
        document.body.classList.add("sidebar-collapsed-state");
        document.body.classList.add("vertical-collpsed");
      } else {
        document.body.classList.remove("sidebar-collapsed-state");
        document.body.classList.remove("vertical-collpsed");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Activate menu based on current path
  const pathName = (props.location.pathname || "").split("?")[0];

  const urlMappings = {
    "/myteam/addnewuser": "/myteam/viewall",
    "/myteam/updateuser": "/myteam/viewall",
    "/candidate/add": "/candidates",
    "/candidate/:candidateId/edit": "/candidates",
    "/importcandidates": "/candidates",
    "/matchcandidate": "/candidates",
    "/job/add": "/job",
    "/job/:jobId/edit": "/job",
    "/importjob": "/job",
    "/job/open": "/job",
    "/job/closed": "/job",
    "/match": "/job",
    "/recruiter/dashboard": "/recruiter/viewall",
    "/recruiter/addorganization": "/recruiter/viewall",
    "/recruiter/editorganization": "/recruiter/viewall",
    "/recruiter/job/addjob": "/recruiter/job/viewall",
    "/recruiter/job/updatejob": "/recruiter/job/viewall",
    "/recruiter/job/importjob": "/recruiter/job/viewall",
    "/recruiter/job/match": "/recruiter/job/viewall",
    "/recruiter/candidate/addcandidate": "/recruiter/candidate/viewall",
    "/recruiter/candidate/updatecandidate": "/recruiter/candidate/viewall",
    "/recruiter/candidate/bulkimport": "/recruiter/candidate/viewall",
    "/email/updateEmail": "/email/viewemailcontent",
    "/email/addnewemail": "/email/viewemailcontent",
    "/setting/editsettings": "/setting/viewSettings",
    "/setting/addnewsetting": "/setting/viewSettings",
  };

  let mappedUrl = urlMappings[pathName] || pathName;

  if (!urlMappings[pathName]) {
    if (pathName === "/jobs") {
      mappedUrl = "/job";
    } else if (pathName.startsWith("/job/")) {
      mappedUrl = "/job";
    } else if (pathName === "/candidate") {
      mappedUrl = "/candidates";
    } else if (pathName.startsWith("/candidate/")) {
      mappedUrl = "/candidates";
    } else if (pathName.startsWith("/recruiter/job/")) {
      mappedUrl = "/recruiter/job/viewall";
    } else if (pathName.startsWith("/recruiter/candidate/")) {
      mappedUrl = "/recruiter/candidate/viewall";
    }
  }

  const isSubMenuActive = (subMenus) => {
    if (!subMenus || !Array.isArray(subMenus)) return false;
    return subMenus.some((subMenu) => {
      if (!subMenu.url || subMenu.url === "#") return false;
      let subPath = subMenu.url.split("?")[0].replace(/\/$/, "");
      let subMapped = urlMappings[subPath] || subPath;
      if (!urlMappings[subPath]) {
        if (subPath === "/jobs") subMapped = "/job";
        else if (subPath.startsWith("/job/")) subMapped = "/job";
        else if (subPath === "/candidate") subMapped = "/candidates";
        else if (subPath.startsWith("/candidate/")) subMapped = "/candidates";
        else if (subPath.startsWith("/recruiter/job/")) subMapped = "/recruiter/job/viewall";
        else if (subPath.startsWith("/recruiter/candidate/")) subMapped = "/recruiter/candidate/viewall";
      }
      return subPath === pathName || subMapped === pathName || subMapped === mappedUrl || subPath === mappedUrl;
    });
  };

  useEffect(() => {

    // Small delay to ensure DOM is rendered
    const timer = setTimeout(() => {
      // Only remove menu-active, let React handle .active via className
      const allItems = document.querySelectorAll("#side-menu .nav-menu-item");
      allItems.forEach((item) => item.classList.remove("menu-active"));

      // Find matching menu item
      const menuLinks = document.querySelectorAll(
        "#side-menu .nav-menu-link:not(.has-submenu)",
      );
      let matchingMenuItem = null;
      let exactMatchItem = null;

      menuLinks.forEach((link) => {
        let lPath = link.pathname.replace(/\/$/, "");
        let mPath = mappedUrl.replace(/\/$/, "");
        let pPath = pathName.replace(/\/$/, "");

        if (lPath === "/jobs") lPath = "/job";
        if (lPath === "/candidate") lPath = "/candidates";
        if (pPath === "/jobs") pPath = "/job";
        if (mPath === "/jobs") mPath = "/job";

        // Also normalize the link's path using urlMappings if available
        let lMapped = urlMappings[lPath] || lPath;
        if (!urlMappings[lPath]) {
          if (lPath.startsWith("/job/")) lMapped = "/job";
          else if (lPath.startsWith("/candidate/")) lMapped = "/candidates";
          else if (lPath.startsWith("/recruiter/job/")) lMapped = "/recruiter/job/viewall";
          else if (lPath.startsWith("/recruiter/candidate/")) lMapped = "/recruiter/candidate/viewall";
        }

        if (lPath === pPath) {
          exactMatchItem = link;
        } else if (!exactMatchItem && lMapped === pPath) {
          exactMatchItem = link;
        } else if (!matchingMenuItem && (lMapped === mPath || lPath === mPath)) {
          matchingMenuItem = link;
        }
      });

      matchingMenuItem = exactMatchItem || matchingMenuItem;

      if (matchingMenuItem) {
        matchingMenuItem.classList.add("active");
        const parentItem = matchingMenuItem.closest(".nav-menu-item");
        if (parentItem) {
          parentItem.classList.add("menu-active");
          // Auto-expand parent submenu if exists
          const parentSubmenu = matchingMenuItem.closest(".submenu-list");
          if (parentSubmenu) {
            const parentMenuItem = parentSubmenu.closest(".nav-menu-item");
            if (parentMenuItem) {
              const parentIndex = Array.from(
                parentMenuItem.parentElement.children,
              ).indexOf(parentMenuItem);
              setActiveMenuIndex(parentIndex);
            }
          } else {
            setActiveMenuIndex(null);
          }
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [props.location.pathname]);

  // Recalculate scroll on state change
  useEffect(() => {
    if (scrollRef.current) {
      const timer = setTimeout(() => {
        scrollRef.current?.recalculate();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isCollapsed, isHovered, activeMenuIndex]);

  // Outside click handler
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (
        settingsRef.current &&
        !settingsRef.current.contains(e.target) &&
        submenuRef.current &&
        !submenuRef.current.contains(e.target)
      ) {
        setSettingsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setProfileOpen(false);
        setSettingsOpen(false);
        setActiveMenuIndex(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Handle menu click
  const handleMenuClick = (index) => {
    if (isCollapsed) {
      setIsCollapsed(false);
      document.body.classList.remove("sidebar-collapsed-state");
      document.body.classList.remove("vertical-collpsed");
      setActiveMenuIndex(index);
    } else {
      setActiveMenuIndex(activeMenuIndex === index ? null : index);
    }
  };

  // Handle profile click
  const handleProfileClick = (e) => {
    e.stopPropagation();
    setProfileOpen(!profileOpen);
    setSettingsOpen(false);
  };

  // Handle settings click
  const handleSettingsClick = (e) => {
    e.stopPropagation();
    setSettingsOpen(!settingsOpen);
    setProfileOpen(false);
  };

  const handleMobileMenuItemClick = () => { };

  const currentPath = props.location.pathname;

  // Determine if sidebar should show expanded content
  const showExpanded = !isCollapsed;

  const handleToggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    if (newState) {
      document.body.classList.add("sidebar-collapsed-state");
      document.body.classList.add("vertical-collpsed");
    } else {
      document.body.classList.remove("sidebar-collapsed-state");
      document.body.classList.remove("vertical-collpsed");
    }
  };

  return (
    <React.Fragment>
      {/* Main Sidebar */}
      <aside
        ref={sidebarRef}
        className={`modern-sidebar ${showExpanded ? "expanded" : "collapsed"}`}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Header Section */}
        <div
          className="sidebar-header-section"
          style={{
            display: 'flex',
            flexDirection: showExpanded ? 'row' : 'column',
            justifyContent: showExpanded ? 'space-between' : 'center',
            alignItems: 'center',
            padding: '10px 15px',
            gap: showExpanded ? '0' : '10px'
          }}
        >
          <div className="sidebar-brand" style={{ display: 'flex', alignItems: 'center' }}>
            {!showExpanded && (
              <div className="brand-icon-wrapper">
                <img src={logo} alt="brand logo" width={40} />
              </div>
            )}
            {showExpanded && (
              <span className="brand-name">
                <img src={brandName} alt="HireNeuron" width={120} />
              </span>
            )}
          </div>
          <button
            onClick={handleToggleCollapse}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "5px",
              paddingTop: "15px",
              color: "inherit",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            <FiMenu size={24} />
          </button>
        </div>

        {/* Navigation Menu */}
        <SimpleBar ref={scrollRef} className="sidebar-nav-container">
          <nav id="sidebar-menu" aria-label="Main menu">
            <ul className="nav-menu-list" id="side-menu" role="menubar">
              {leftMenus.map((option, index) => (
                <React.Fragment key={option.id || index}>
                  {option?.subMenus?.length > 0 ? (
                    <li
                      className={`nav-menu-item ${activeMenuIndex === index ? "expanded" : ""
                        }`}
                      role="none"
                    >
                      <Link
                        to="#"
                        className={`nav-menu-link has-submenu ${activeMenuIndex === index || isSubMenuActive(option.subMenus) ? 'active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleMenuClick(index);
                        }}
                        title={!showExpanded ? props.t(option.name) : ""}
                        role="menuitem"
                        aria-expanded={activeMenuIndex === index}
                        aria-haspopup="true"
                      >
                        <i
                          className={`menu-icon mdi ${getMenuIcon(option.name, option.icon)}`}
                          aria-hidden="true"
                        />
                        {showExpanded && (
                          <>
                            <span className="menu-text">
                              {props.t(option.name)}
                            </span>
                            <span className="menu-arrow">
                              <FiChevronDown size={16} />
                            </span>
                          </>
                        )}
                      </Link>

                      {/* Only render submenu if expanded */}
                      {activeMenuIndex === index && showExpanded && (
                        <ul
                          className="submenu-list"
                          role="menu"
                          aria-label={`${option.name} submenu`}
                        >
                          {[...(option.subMenus || [])]
                            .sort(
                              (a, b) => (a?.priority || 0) - (b?.priority || 0),
                            )
                            .map((subMenu) => (
                              <li key={subMenu.id || subMenu.url} role="none">
                                {subMenu?.urlType === "EXTERNAL" ? (
                                  <a
                                    href={subMenu.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`submenu-link ${subMenu.url === "/logout"
                                      ? "danger-link"
                                      : ""
                                      }`}
                                    role="menuitem"
                                    onClick={handleMobileMenuItemClick}
                                  >
                                    <span>{props.t(subMenu.name)}</span>
                                  </a>
                                ) : (
                                  <Link
                                    to={subMenu.url}
                                    className={`submenu-link ${subMenu.url === "/logout"
                                      ? "danger-link"
                                      : ""
                                      }`}
                                    role="menuitem"
                                    onClick={handleMobileMenuItemClick}
                                  >
                                    <span>{props.t(subMenu.name)}</span>
                                  </Link>
                                )}
                              </li>
                            ))}
                        </ul>
                      )}
                    </li>
                  ) : (
                    <li className="nav-menu-item" role="none">
                      <Link
                        to={
                          option.url === "/candidate"
                            ? "/candidates"
                            : option.url === "/jobs"
                              ? "/job"
                              : option.url
                        }
                        className={`nav-menu-link ${(option.name?.toLowerCase() === "jobs" || option.name?.toLowerCase() === "job") && mappedUrl === "/job"
                          ? "active"
                          : isMatchingPath(
                            mappedUrl,
                            (urlMappings[option.url] || option.url) === "/candidate"
                              ? "/candidates"
                              : (urlMappings[option.url] || option.url)?.startsWith("/job/") || (urlMappings[option.url] || option.url) === "/jobs"
                                ? "/job"
                                : (urlMappings[option.url] || option.url)
                          )
                            ? "active"
                            : ""
                          }`}
                        title={!showExpanded ? props.t(option.name) : ""}
                        role="menuitem"
                        onClick={handleMobileMenuItemClick}
                      >
                        <i
                          className={`menu-icon mdi ${getMenuIcon(option.name, option.icon)}`}
                          aria-hidden="true"
                        />
                        {showExpanded && (
                          <span className="menu-text">
                            {props.t(option.name)}
                          </span>
                        )}
                      </Link>
                    </li>
                  )}
                </React.Fragment>
              ))}
            </ul>
          </nav>
        </SimpleBar>

        {/* Bottom Section */}
        {/* <div className="sidebar-footer-section" ref={footerRef}> */}
        {/* Settings Submenu with scroll support */}
        {/* {settingsOpen &&
            settingsMenu?.subMenus?.length > 0 &&
            showExpanded && (
              <div
                ref={submenuRef}
                className="footer-submenu"
                role="menu"
                aria-label="Settings menu"
              >
                <div className="footer-submenu-header">Settings</div>
                <div className="footer-submenu-scroll">
                  {[...settingsMenu.subMenus]
                    .sort((a, b) => (a?.priority || 0) - (b?.priority || 0))
                    .map((subMenu, idx) => (
                      <Link
                        key={subMenu.id || idx}
                        to={subMenu?.url || "#"}
                        className="footer-submenu-link"
                        onClick={() => {
                          setSettingsOpen(false);
                          handleMobileMenuItemClick();
                        }}
                        role="menuitem"
                      >
                        {subMenu?.icon && (
                          <i
                            className={`mdi ${subMenu.icon}`}
                            aria-hidden="true"
                          />
                        )}
                        <span>{props.t(subMenu?.name)}</span>
                      </Link>
                    ))}
                </div>
              </div>
            )} */}

        {/* Settings Button */}
        {/* {settingsMenu && (
            <button
              ref={settingsRef}
              className={`footer-action-btn ${settingsOpen ? "active" : ""}`}
              onClick={handleSettingsClick}
              title={!showExpanded ? "Settings" : ""}
              aria-label="Settings"
              aria-expanded={settingsOpen}
            >
              <span className="action-icon">
                <i
                  className={`mdi ${settingsMenu.icon || "mdi-cog-outline"}`}
                  aria-hidden="true"
                />
              </span>
              {showExpanded && (
                <span className="action-label">
                  {props.t(settingsMenu.name) || "Settings"}
                </span>
              )}
            </button>
          )} */}

        {/* Profile Button */}
        {/* <button
            ref={profileRef}
            className={`footer-action-btn profile-btn ${profileOpen ? "active" : ""}`}
            onClick={handleProfileClick}
            title={!showExpanded && verifyData(user) ? user.name : ""}
            aria-label="User profile"
            aria-expanded={profileOpen}
          >
            <span className="action-icon">
              <img
                src={
                  verifyData(user) && user.profileImage
                    ? user.profileImage
                    : defaultProfile
                }
                alt={`${verifyData(user) ? user.name : "User"} avatar`}
                className="profile-avatar"
              />
            </span>
            {showExpanded && (
              <span className="action-label">
                <span className="profile-display-name" translate="no">
                  {verifyData(user) ? user.name : "Profile"}
                </span>
                <span className="profile-display-role">
                  {verifyData(user) ? user.roleName : ""}
                </span>
              </span>
            )}
          </button> */}
        {/* </div> */}
      </aside>
    </React.Fragment>
  );
};

Sidebar.propTypes = {
  location: PropTypes.object,
  t: PropTypes.func,
  collapsed: PropTypes.bool,
  layout: PropTypes.object,
};

const mapStatetoProps = (state) => {
  return { layout: state.Layout };
};

export default connect(
  mapStatetoProps,
  {},
)(withRouter(withTranslation()(Sidebar)));
